import { Component, EventEmitter, Input, Output } from '@angular/core';
import { SideMenuNode, SideMenuSelectEvent } from '../../../../shared/components/sidemenu/sidemenu.model';

export interface VisualDraft {
  title: string;
  width: number;
}

const EMPTY_VISUAL_DRAFT: VisualDraft = { title: '', width: 6 };

/**
 * Body of the "Add visual" popup - same ASK / Visual Editor / JSON Editor / SQL Query shell as
 * AddFilterModalComponent, reused for every visual type; only the header label changes per type
 * (set via `visualTypeLabel` from the grid item the user clicked in InbuiltCustomReportComponent).
 */
@Component({
  selector: 'app-add-visual-modal',
  standalone: false,
  templateUrl: './add-visual-modal.component.html',
  styleUrl: './add-visual-modal.component.scss'
})
export class AddVisualModalComponent {
  @Input() visualTypeLabel = '';
  @Output() cancelled = new EventEmitter<void>();
  @Output() visualCreated = new EventEmitter<any>();

  readonly menuItems: SideMenuNode[] = [
    { id: 'ask', type: 'ask', name: 'ASK' },
    { id: 'visual-editor', type: 'editor', name: 'Visual Editor' },
    { id: 'json-editor', type: 'editor', name: 'JSON Editor' },
    { id: 'sql-query', type: 'query', name: 'SQL Query' }
  ];
  selectedMenuId = 'ask';

  askPromptValue = '';
  draft: VisualDraft = { ...EMPTY_VISUAL_DRAFT };
  jsonEditorInitialJson: any = {};
  sqlQueryCode = '';

  get modalTitle(): string {
    return this.visualTypeLabel ? `Add ${this.visualTypeLabel}` : 'Add visual';
  }

  onMenuSelected(event: SideMenuSelectEvent): void {
    if (event?.node?.id) {
      this.selectedMenuId = event.node.id;
    }
  }

  // app-modal keeps this component mounted between opens (see select-datasource-modal-pattern memory) -
  // the host must call this before showing the modal again so a previous draft isn't still here.
  resetState(): void {
    this.selectedMenuId = 'ask';
    this.askPromptValue = '';
    this.draft = { ...EMPTY_VISUAL_DRAFT };
    this.jsonEditorInitialJson = {};
    this.sqlQueryCode = '';
  }

  onJsonTextChange(value: any): void {
    this.jsonEditorInitialJson = value;
  }

  get primaryActionLabel(): string {
    return this.selectedMenuId === 'ask' ? 'Generate' : 'Add visual';
  }

  get canPerformPrimaryAction(): boolean {
    if (this.selectedMenuId === 'ask') {
      return !!this.askPromptValue.trim();
    }
    return !!this.draft.title.trim();
  }

  onPrimaryAction(): void {
    if (this.selectedMenuId === 'ask') {
      // TODO: wire to the real ASK-generation endpoint once available; for now just move to Visual Editor for review.
      this.selectedMenuId = 'visual-editor';
      return;
    }
    this.visualCreated.emit({
      draft: this.draft,
      jsonEditor: this.jsonEditorInitialJson,
      sqlQuery: this.sqlQueryCode
    });
  }

  onCancel(): void {
    this.cancelled.emit();
  }
}
